// src/components/personal/sections/OrdersSection/components/Search.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { COMPONENT_STYLES } from '@/theme/components';
import { TEXT_STYLES } from '@/theme/typography';
import { prepareSearchQuery, isPhoneNumber } from '../utils/phoneUtils';

interface SearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  disabled?: boolean;
  minChars?: number;
  searchOnType?: boolean;
  normalizePhone?: boolean;
}

const Search: React.FC<SearchProps> = ({
  value,
  onChange,
  placeholder = 'Поиск',
  debounceMs = 800,
  disabled = false,
  minChars = 1,
  searchOnType = false,
  normalizePhone = true
}) => {
  // Локальный ввод пользователя
  const [inputValue, setInputValue] = useState(value);
  
  // Сохраняем предыдущее значение для сравнения
  const prevInputValueRef = useRef(value);
  
  // Состояние UI для индикации загрузки
  const [isSearching, setIsSearching] = useState(false);
  
  // Ref для хранения таймера дебаунса
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Принудительно синхронизируем входное значение, когда оно меняется извне
  useEffect(() => {
    setInputValue(value);
    prevInputValueRef.current = value;
    
    // Сбрасываем состояние поиска, если значение пустое
    if (value === '') {
      setIsSearching(false);
    }
  }, [value]);
  
  // Проверяем, изменилось ли значение с учетом нормализации
  const hasValueChanged = useCallback((newValue: string, oldValue: string) => {
    if (newValue === oldValue) {
      return false;
    }
    
    // Если оба значения - телефоны, сравниваем их нормализованные версии
    if (normalizePhone && isPhoneNumber(newValue) && isPhoneNumber(oldValue)) {
      const normalizedNew = prepareSearchQuery(newValue);
      const normalizedOld = prepareSearchQuery(oldValue);
      return normalizedNew !== normalizedOld;
    }
    
    // В остальных случаях сравниваем обычным образом
    return true;
  }, [normalizePhone]);
  
  // Функция отправки поиска
  const submitSearch = useCallback((query: string) => {
    if (disabled) return;
    
    // Если значение не изменилось, не отправляем запрос
    const valueChanged = hasValueChanged(query, prevInputValueRef.current);
    
    if (!valueChanged) {
      return;
    }
    
    // Если включена нормализация телефонов и это похоже на телефон, обрабатываем запрос
    let processedQuery = query;
    if (normalizePhone && isPhoneNumber(query)) {
      processedQuery = prepareSearchQuery(query);
    }
    
    // Отправляем новый запрос
    onChange(processedQuery);
    
    // Обновляем предыдущее значение
    prevInputValueRef.current = query;
    
    // Показываем индикатор поиска ненадолго
    if (processedQuery) {
      setIsSearching(true);
      setTimeout(() => setIsSearching(false), 500);
    } else {
      setIsSearching(false);
    }
  }, [onChange, disabled, hasValueChanged, normalizePhone]);
  
  // Обработчик изменения ввода
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const newValue = e.target.value;
    
    // Обновляем локальное состояние немедленно
    setInputValue(newValue);
    
    // Очищаем предыдущий таймер если он был
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Для пустой строки отправляем поиск сразу
    if (newValue === '') {
      submitSearch('');
      return;
    }
    
    // Проверяем минимальную длину для запуска поиска
    let effectiveLength = newValue.length;
    let effectiveMinChars = minChars;
    
    // Если это телефонный номер, проверяем длину только цифр
    if (normalizePhone && isPhoneNumber(newValue)) {
      const digitsOnly = newValue.replace(/\D/g, '');
      effectiveLength = digitsOnly.length;
      // Для телефонов снижаем порог до 4 цифр, если оригинальный порог был выше
      effectiveMinChars = Math.min(minChars, 4);
    }
    
    // Не запускаем поиск, если меньше минимального количества символов
    if (effectiveLength < effectiveMinChars) {
      return;
    }
    
    // Запускаем поиск по таймеру только если включена опция searchOnType
    if (searchOnType) {
      // Устанавливаем новый таймер
      debounceTimerRef.current = setTimeout(() => {
        submitSearch(newValue);
      }, debounceMs);
    }
  };
  
  // Обработчик нажатия Enter - немедленная отправка
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !disabled) {
      e.preventDefault();
      
      // Очищаем таймер, если он есть
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      
      const query = inputValue;
      
      // Проверяем минимальную длину для запуска поиска
      let effectiveLength = query.length;
      let effectiveMinChars = minChars;
      
      // Если это телефонный номер, проверяем длину только цифр
      if (normalizePhone && isPhoneNumber(query)) {
        const digitsOnly = query.replace(/\D/g, '');
        effectiveLength = digitsOnly.length;
        // Для телефонов снижаем порог до 4 цифр, если оригинальный порог был выше
        effectiveMinChars = Math.min(minChars, 4);
      }
      
      // Для пустой строки или достаточно длинной строки отправляем поиск
      if (query === '' || effectiveLength >= effectiveMinChars) {
        submitSearch(query);
      }
    }
  };
  
  // Обработчик потери фокуса
  const handleBlur = () => {
    if (disabled) return;
    
    // Очищаем таймер, если он есть
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    const query = inputValue;
    
    // Проверяем минимальную длину для запуска поиска
    let effectiveLength = query.length;
    let effectiveMinChars = minChars;
    
    // Если это телефонный номер, проверяем длину только цифр
    if (normalizePhone && isPhoneNumber(query)) {
      const digitsOnly = query.replace(/\D/g, '');
      effectiveLength = digitsOnly.length;
      // Для телефонов снижаем порог до 4 цифр, если оригинальный порог был выше
      effectiveMinChars = Math.min(minChars, 4);
    }
    
    // Проверяем, изменилось ли значение
    const valueChanged = hasValueChanged(query, prevInputValueRef.current);
    
    // Отправляем поиск только если значение изменилось и соответствует минимальным критериям
    if (valueChanged && (query === '' || effectiveLength >= effectiveMinChars)) {
      submitSearch(query);
    }
  };
  
  return (
    <div className={COMPONENT_STYLES.toolbar.searchContainer}>
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`${COMPONENT_STYLES.input.search} ${TEXT_STYLES.body} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        style={{ minWidth: '170px' }} 
        disabled={disabled}
        autoComplete="off"
        data-testid="search-input"
      />
      
      <SearchIcon 
        className={`${COMPONENT_STYLES.icon.search} ${disabled ? 'opacity-60' : ''} ${
          isSearching ? 'text-primary animate-pulse' : ''
        }`}
      />
      
      {/* Индикатор загрузки */}
      {isSearching && (
        <span className="absolute right-10 top-1/2 -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-primary rounded-full border-t-transparent"></div>
        </span>
      )}
    </div>
  );
};

export default Search;